import LegalPageLayout, { Sec, List } from "@/components/legal/LegalPageLayout";
import { LEGAL_DOCUMENTS } from "@/lib/legalDocuments";

export default function Cookies() {
  return (
    <LegalPageLayout
      document={LEGAL_DOCUMENTS.cookies}
      seoDescription="Cookie Policy for Hannah Howard Funding — how we use cookies and similar technologies."
    >
      <Sec n="1" title="Purpose">
        <p>This Cookie Policy explains how Hannah Howard Funding ("Hannah Howard Funding," "we," "our," or "us") uses cookies and similar technologies when you visit or interact with our website and related online services (collectively, the "Services").</p>
        <p>This Cookie Policy should be read together with our Privacy Policy.</p>
      </Sec>

      <Sec n="2" title="What Are Cookies?">
        <p>Cookies are small text files stored on your device when you visit a website. Cookies help websites recognize your browser, remember preferences, improve performance, enhance security, and better understand how visitors use the Services.</p>
        <p>We may also use similar technologies such as:</p>
        <List items={[
          "Pixels",
          "Web Beacons",
          "Local Storage",
          "Session Storage",
          "Tags",
          "Device Identifiers",
          "Software Development Kits (SDKs), where applicable",
        ]}/>
        <p>Throughout this Policy, these technologies are collectively referred to as "Cookies."</p>
      </Sec>

      <Sec n="3" title="Types of Cookies We May Use">
        <h3 className="font-semibold text-[#2A1C14] mt-4 mb-2">Strictly Necessary Cookies</h3>
        <p>These cookies are required for the website to function properly.</p>
        <p>They may be used for:</p>
        <List items={[
          "maintaining secure sessions;",
          "preventing fraud;",
          "load balancing;",
          "website security;",
          "form submissions;",
          "navigation.",
        ]}/>
        <p>Because these cookies are necessary for operation of the Services, they generally cannot be disabled through our website.</p>

        <h3 className="font-semibold text-[#2A1C14] mt-6 mb-2">Functional Cookies</h3>
        <p>These cookies remember choices you make to improve your experience.</p>
        <p>Examples include:</p>
        <List items={[
          "remembering form progress;",
          "language preferences;",
          "accessibility settings;",
          "previously entered information where appropriate.",
        ]}/>

        <h3 className="font-semibold text-[#2A1C14] mt-6 mb-2">Performance and Analytics Cookies</h3>
        <p>These cookies help us understand how visitors interact with the Services.</p>
        <p>Information collected may include:</p>
        <List items={[
          "pages viewed;",
          "navigation paths;",
          "referral sources;",
          "browser type;",
          "operating system;",
          "session duration;",
          "approximate geographic location derived from IP address;",
          "website performance metrics.",
        ]}/>
        <p>This information is generally used in aggregated or de-identified form to improve our Services.</p>

        <h3 className="font-semibold text-[#2A1C14] mt-6 mb-2">Marketing and Advertising Cookies</h3>
        <p>If implemented, these cookies may help measure advertising effectiveness and improve the relevance of promotional content.</p>
        <p>Depending on our marketing activities, these technologies may be used to:</p>
        <List items={[
          "measure advertising campaigns;",
          "understand website conversions;",
          "prevent duplicate advertisements;",
          "evaluate marketing performance.",
        ]}/>
        <p>We do not guarantee that advertising technologies will always be active, and they may be added, removed, or modified over time.</p>
      </Sec>

      <Sec n="4" title="Third-Party Technologies">
        <p>We may utilize third-party technologies that place or access cookies on our behalf.</p>
        <p>Examples may include providers of:</p>
        <List items={[
          "website hosting;",
          "analytics;",
          "customer relationship management (CRM);",
          "appointment scheduling;",
          "customer support;",
          "fraud prevention;",
          "payment processing;",
          "email communications;",
          "SMS communications;",
          "website security.",
        ]}/>
        <p>Each third party maintains its own privacy practices governing information collected through its services.</p>
      </Sec>

      <Sec n="5" title="Managing Cookies">
        <p>Most web browsers allow you to:</p>
        <List items={[
          "block cookies;",
          "delete existing cookies;",
          "receive notifications before cookies are stored;",
          "configure cookie preferences.",
        ]}/>
        <p>Disabling cookies may affect certain website functionality, including application forms, login sessions, security features, or personalized settings.</p>
        <p>
          You may also manage cookie preferences on this site at any time via the{" "}
          <button
            type="button"
            data-testid="open-cookie-preferences-link"
            onClick={() => window.dispatchEvent(new CustomEvent("hh:open-cookie-preferences"))}
            className="underline hover:text-[#2A1C14]"
          >
            Cookie Settings
          </button>{" "}
          panel.
        </p>
      </Sec>

      <Sec n="6" title="Browser Controls">
        <p>Cookie settings are generally available within your browser's privacy or security settings.</p>
        <p>Instructions vary by browser and device.</p>
        <p>For information about your specific browser, consult the documentation provided by the browser developer.</p>
      </Sec>

      <Sec n="7" title="Do Not Track Signals">
        <p>Some browsers transmit "Do Not Track" ("DNT") signals.</p>
        <p>Because there is currently no universally accepted industry standard governing DNT responses, the Services may not respond differently to such signals unless required by applicable law.</p>
      </Sec>

      <Sec n="8" title="Changes to this Cookie Policy">
        <p>We may update this Cookie Policy periodically to reflect changes in our Services, technology, legal requirements, or business practices.</p>
        <p>The "Effective Date" above indicates the date of the most recent revision.</p>
        <p>Continued use of the Services following publication of updates constitutes acknowledgment of the revised Cookie Policy.</p>
      </Sec>

      <Sec n="9" title="Contact Us">
        <p>Questions regarding this Cookie Policy may be directed to:</p>
        <p>
          Hannah Howard Funding<br />
          Email: hello@hannahhowardfunding.com<br />
          Phone: (870) 919-9520<br />
          Mailing Address: 495 Brickell Ave Miami, FL 33131
        </p>
      </Sec>
    </LegalPageLayout>
  );
}
